import React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Text,
  VStack,
  keyframes,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import logoHeader from '../../images/logo-header.png';

const NAVY = '#0F1F6B';
const BLUE = '#1E88E5';
const ORANGE = '#FF6A13';

interface MaintenancePageProps {
  title: string;
  message: string;
  retryAfterSeconds: number;
  onRetry?: () => void;
  onSignOut?: () => void;
}

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(255,106,19,.55); }
  70%  { box-shadow: 0 0 0 9px rgba(255,106,19,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,106,19,0); }
`;

const slide = keyframes`
  0%   { background-position: 130% 0; }
  100% { background-position: -30% 0; }
`;

export function formatRetryWindow(seconds: number): string {
  if (!seconds || seconds <= 0) return 'Please try again shortly.';

  const minutes = Math.round(seconds / 60);
  if (minutes < 2) return 'Please try again in a moment.';
  if (minutes < 60) return `Please try again in about ${minutes} minutes.`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return 'Please try again in about an hour.';
  return `Please try again in about ${hours} hours.`;
}

function MaintenancePage({
  title,
  message,
  retryAfterSeconds,
  onRetry,
  onSignOut,
}: MaintenancePageProps): JSX.Element {
  const reduceMotion = usePrefersReducedMotion();

  const pulseAnimation = reduceMotion
    ? undefined
    : `${pulse} 1.8s ease-out infinite`;
  const slideAnimation = reduceMotion
    ? undefined
    : `${slide} 2.4s linear infinite`;

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      px={6}
      py={10}
      bg="gray.50"
    >
      <VStack as="main" spacing={8} w="100%" maxW="480px">
        <Image
          src={logoHeader}
          alt="Uptrust"
          h="44px"
          maxW="220px"
          objectFit="contain"
        />

        <Box
          w="100%"
          bg="white"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="xl"
          boxShadow="lg"
          px={{ base: 6, md: 10 }}
          py={{ base: 8, md: 10 }}
          textAlign="center"
        >
          <HStack
            spacing={2}
            display="inline-flex"
            bg="orange.50"
            color={ORANGE}
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="wide"
            mb={6}
          >
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={ORANGE}
              animation={pulseAnimation}
              aria-hidden="true"
            />
            <Text as="span">Upgrade in progress</Text>
          </HStack>

          <Heading as="h1" size="lg" color={NAVY} mb={4} letterSpacing="tight">
            {title}
          </Heading>

          <Text color="gray.600" fontSize="md" lineHeight="tall" mb={2}>
            {message}
          </Text>

          <Text color="gray.700" fontSize="md" fontWeight="medium" mb={7}>
            {formatRetryWindow(retryAfterSeconds)}
          </Text>

          <Box
            h="4px"
            borderRadius="full"
            mb={8}
            bgGradient={`linear(to-r, gray.200 0%, ${BLUE} 50%, gray.200 100%)`}
            backgroundSize="220% 100%"
            animation={slideAnimation}
            aria-hidden="true"
          />

          <VStack spacing={3}>
            {onRetry && (
              <Button w="100%" variant="outline" color={BLUE} onClick={onRetry}>
                Try again
              </Button>
            )}
            {onSignOut && (
              <Button
                w="100%"
                variant="outline"
                borderColor={BLUE}
                color={BLUE}
                onClick={onSignOut}
              >
                Sign out
              </Button>
            )}
          </VStack>
        </Box>
      </VStack>
    </Flex>
  );
}

MaintenancePage.defaultProps = {
  onRetry: undefined,
  onSignOut: undefined,
};

export default MaintenancePage;
